import { Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useWishlistStore } from "@/stores/wishlistStore";
import { ShopifyProduct } from "@/lib/shopify";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import React from "react";

interface WishlistButtonProps {
  product: ShopifyProduct;
  size?: "sm" | "md" | "lg";
  className?: string;
  showLabel?: boolean;
}

export const WishlistButton = React.forwardRef<HTMLButtonElement, WishlistButtonProps>(({ 
  product, 
  size = "md", 
  className,
  showLabel = false 
}, ref) => {
  const { isInWishlist, toggleItem } = useWishlistStore();
  const isFavorite = isInWishlist(product.node.id);

  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    toggleItem(product);
    
    if (isFavorite) {
      toast.info("Removed from favorites", {
        description: product.node.title,
        position: "top-center",
      });
    } else {
      toast.success("Added to favorites", {
        description: product.node.title,
        position: "top-center",
        icon: "❤️",
      });
    }
  };

  return (
    <motion.button
      ref={ref}
      onClick={handleClick}
      className={cn(
        "flex items-center justify-center gap-2 rounded-full bg-background/80 backdrop-blur-sm border border-border shadow-sm transition-all hover:bg-background",
        sizeClasses[size],
        showLabel && "w-auto px-4",
        className
      )}
      whileTap={{ scale: 0.9 }}
      aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={isFavorite ? "filled" : "empty"}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0 }}
          transition={{ duration: 0.15 }}
        >
          <Heart
            className={cn(
              iconSizes[size],
              "transition-colors",
              isFavorite 
                ? "fill-destructive text-destructive" 
                : "text-muted-foreground hover:text-destructive"
            )}
          />
        </motion.div>
      </AnimatePresence>
      {showLabel && (
        <span className="text-sm font-medium">
          {isFavorite ? "Favorited" : "Add"}
        </span>
      )}
    </motion.button>
  );
});

WishlistButton.displayName = "WishlistButton";
